import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Slider,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "../components/ThemeContext";

const VideoTrimmer = ({ videoSrc, onTrimComplete }) => {
  const [duration, setDuration] = useState(0);
  const [trimRange, setTrimRange] = useState([0, 100]);
  const [videoSize, setVideoSize] = useState(null);
  const [estimatedSize, setEstimatedSize] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.onloadedmetadata = () => {
        setDuration(videoRef.current.duration);
        setIsLoading(false);
      };

      // Get the video file size
      const getVideoSize = async () => {
        try {
          const response = await fetch(videoSrc);
          const blob = await response.blob();
          const size = blob.size;
          setVideoSize(size);
          updateEstimatedSize(size, 0, 100);
        } catch (error) {
          console.error("Error fetching video size:", error);
        }
      };

      getVideoSize();
    }
  }, [videoSrc]);

  // Update estimated size when trim range changes
  useEffect(() => {
    if (videoSize !== null && duration > 0) {
      updateEstimatedSize(videoSize, trimRange[0], trimRange[1]);
    }
  }, [trimRange, videoSize, duration]);

  const updateEstimatedSize = (originalSize, startPercent, endPercent) => {
    // Calculate the percentage of the video we're keeping
    const keepPercentage = (endPercent - startPercent) / 100;
    // Estimate the new file size (this is approximate)
    const estimatedNewSize = originalSize * keepPercentage;
    setEstimatedSize(estimatedNewSize);
  };

  const handleRangeChange = (event, newValue) => {
    setTrimRange(newValue);

    // Update video playback position when sliding
    if (videoRef.current) {
      const startTime = (newValue[0] / 100) * duration;
      videoRef.current.currentTime = startTime;
    }
  };

  const handleTrimComplete = () => {
    const startTime = (trimRange[0] / 100) * duration;
    const endTime = (trimRange[1] / 100) * duration;
    onTrimComplete(startTime, endTime);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isFileSizeOK = estimatedSize && estimatedSize < 10 * 1024 * 1024; // Less than 10MB

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      <video
        ref={videoRef}
        src={videoSrc}
        controls
        style={{
          width: "100%",
          maxHeight: "200px",
          display: isLoading ? "none" : "block",
        }}
      />

      {!isLoading && (
        <>
          <Box sx={{ px: 2, mt: 2 }}>
            <Slider
              value={trimRange}
              onChange={handleRangeChange}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => formatTime((value / 100) * duration)}
              step={1}
              min={0}
              max={100}
              sx={{
                color: theme.palette.primary.main,
                "& .MuiSlider-thumb": {
                  backgroundColor: theme.palette.primary.main,
                },
                "& .MuiSlider-rail": {
                  backgroundColor: theme.palette.background.gray,
                },
              }}
            />

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
              <Typography variant="caption" color="textSecondary">
                {formatTime((trimRange[0] / 100) * duration)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {formatTime((trimRange[1] / 100) * duration)}
              </Typography>
            </Box>

            {estimatedSize !== null && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography
                  variant="body2"
                  color={isFileSizeOK ? "success.main" : "error.main"}
                  sx={{ fontWeight: "medium" }}>
                  Estimated File Size: {formatFileSize(estimatedSize)}
                  {!isFileSizeOK && " (should be under 10MB)"}
                </Typography>
              </Box>
            )}

            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
              onClick={handleTrimComplete}
              color={isFileSizeOK ? "primary" : "error"}>
              Apply Trim
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default VideoTrimmer;
